import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useEffect } from 'react';
import { HttpError, RQAPICallWithData } from 'store/APIClient';
import { dispatch } from 'store/store';

export const GET_DEFAULT_DASHBOARD_FILTERS_QUERY_KEY =
    'getDefaultDashboardFilters';

const useGetDefaultDashboardFilters = (
    dashboardSlug: string | null | undefined,
): UseQueryResult<DashboardFilterT[], HttpError> => {
    const queryResults = useQuery<DashboardFilterT[], HttpError>({
        queryKey: [GET_DEFAULT_DASHBOARD_FILTERS_QUERY_KEY, dashboardSlug],

        queryFn: async () => {
            const response = await RQAPICallWithData<DashboardFilterT[]>({
                endpoint: `explore/dashboard/${dashboardSlug}/default_filters`,
                method: 'GET',
            });
            return response.content.data;
        },

        enabled: !!dashboardSlug,
        staleTime: 200,
    });

    // CODE-SMELL: This is the only `useQuery` hook that has a condition on the error display.
    // `meta: { useGlobalOnError: true }` does not cover this case, and even though it would be
    // trivial to adapt it to allow for a condition, we choose not to do it to avoid adding complexity
    // for one case.
    // If this behaviour start to be used in several places, we should refactor this to make use of
    // the solution we will choose.
    // In the meantime, we just rely on a useEffect. It is not the most performant solution, but it works.
    useEffect(() => {
        if (queryResults.error && queryResults.error.status !== 401) {
            dispatch.vision.notifyUser({
                message: queryResults.error.message,
                snackType: 'error',
            });
        }
    }, [queryResults.error, dispatch]);

    return queryResults;
};

export default useGetDefaultDashboardFilters;
